import React, { useEffect, useState } from "react";

// Standalone settings component: no application Redux provider is required.
export default function UserCourseSendSelector(props) {
  const [courseId, setCourseId] = useState(props.course_id);
  const [courses, setCourses] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [dayOn, setDayOn] = useState(props.day_on || "");
  const [view, setView] = useState(props.view || "article");
  const [language, setLanguage] = useState(props.language || "javascript");
  const [menuOpen, setMenuOpen] = useState(true);
  const [pending, setPending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [retry, setRetry] = useState(0);
  const available = props.paid_active
    ? [
        "data-structures-and-algorithms",
        "systems-design-and-architecture",
        "object-oriented-programming",
        "machine-learning-fundamentals",
      ]
    : ["data-structures-and-algorithms"];

  useEffect(() => {
    try {
      setMenuOpen(localStorage.getItem("adCourseCurriculumOpen") !== "false");
    } catch (_) {}
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError("");
    const get = async (url) => {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error();
      return response.json();
    };
    Promise.all([
      get("/api/courses"),
      get("/api/courses/" + courseId + "/newsletter"),
    ])
      .then(([allCourses, data]) => {
        setCourses(allCourses);
        setNewsletter(data.newsletter || []);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          setLoadError("We couldn't load your newsletter options.");
          setLoading(false);
        }
      });
    return () => controller.abort();
  }, [courseId, retry]);

  const save = async (changes, message) => {
    if (pending) return;
    setPending(true);
    setError("");
    setNotice("");
    try {
      const response = await fetch("/api/settings", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            ?.content,
        },
        body: JSON.stringify({ brochure: changes }),
      });
      if (!response.ok) throw new Error();
      const data = await response.json();
      if (changes.view !== undefined) setView(data.view || changes.view);
      if (changes.language !== undefined)
        setLanguage(data.language || changes.language);
      if (changes.day_on !== undefined) setDayOn(data.day_on ?? changes.day_on);
      setNotice(message || "Your preferences are saved.");
    } catch (_) {
      setError("Your change wasn't saved. Please try again.");
    } finally {
      setPending(false);
    }
  };
  return (
    <div className="ad-learning-settings">
      <fieldset disabled={pending}>
        <legend>Newsletter</legend>
        <p className="ad-account-fineprint">
          Choose a course and the lesson to receive next. Selecting a lesson
          saves your newsletter preference.
        </p>
        {!props.paid_active && (
          <p className="ad-account-fineprint">
            DS & Algorithms is included with your account. Other newsletters
            require <a href="/subscriptions/new">Premium</a>.
          </p>
        )}
        {loadError ? (
          <div role="alert" className="ad-resource-error">
            {loadError}{" "}
            <button
              type="button"
              className="ad-settings-retry"
              onClick={() => setRetry((value) => value + 1)}
            >
              Try again
            </button>
          </div>
        ) : (
          <div className="ad-account-fields">
            <div>
              <label htmlFor="newsletter-course">Course</label>
              <select
                id="newsletter-course"
                value={courseId}
                disabled={loading}
                onChange={(event) => {
                  setCourseId(event.target.value);
                  setDayOn("");
                  setNotice("Choose your next lesson to save this newsletter.");
                  setError("");
                }}
              >
                {!courses.length && (
                  <option value={courseId}>Loading courses…</option>
                )}
                {courses
                  .filter(
                    (course) =>
                      available.includes(course.slug) ||
                      String(course.id) === String(courseId)
                  )
                  .map((course) => (
                    <option
                      key={course.id}
                      value={course.id}
                      disabled={!available.includes(course.slug)}
                    >
                      {course.newsletter_title || course.title}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label htmlFor="newsletter-day">Next lesson</label>
              <select
                id="newsletter-day"
                value={String(dayOn)}
                disabled={loading || !newsletter.length}
                onChange={(event) =>
                  save(
                    { course_id: Number(courseId), day_on: event.target.value },
                    "Newsletter saved. Your selected lesson will arrive at the next scheduled send."
                  )
                }
              >
                <option value="" disabled>
                  {loading
                    ? "Loading lessons…"
                    : newsletter.length
                    ? "Choose a lesson"
                    : "No lessons available"}
                </option>
                {newsletter.map((item) => (
                  <option key={item[0]} value={item[0]}>
                    {item[0]}. {item[2]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
        <div className="ad-account-divider">
          <h3>Practice defaults</h3>
          <p>These preferences save automatically.</p>
        </div>
        <label className="ad-settings-toggle" htmlFor="viewSwitch">
          <span>
            <strong>Interactive lesson view</strong>
            <small>Open lessons as a guided, step-by-step experience.</small>
          </span>
          <input
            id="viewSwitch"
            type="checkbox"
            checked={view === "interactive"}
            onChange={(event) =>
              save({ view: event.target.checked ? "interactive" : "article" })
            }
          />
        </label>
        <label className="ad-settings-toggle" htmlFor="curriculum-preference">
          <span>
            <strong>Keep the course menu open</strong>
            <small>Show the curriculum sidebar on this browser.</small>
          </span>
          <input
            id="curriculum-preference"
            type="checkbox"
            checked={menuOpen}
            onChange={(event) => {
              try {
                localStorage.setItem(
                  "adCourseCurriculumOpen",
                  String(event.target.checked)
                );
                setMenuOpen(event.target.checked);
                setError("");
                setNotice("Course menu preference saved on this browser.");
              } catch (_) {
                setError(
                  "This browser isn't allowing preferences to be stored."
                );
              }
            }}
          />
        </label>
        <div className="ad-settings-language">
          <label htmlFor="languageSwitch">Default programming language</label>
          <select
            id="languageSwitch"
            value={language}
            onChange={(event) => save({ language: event.target.value })}
          >
            {[
              ["javascript", "JavaScript"],
              ["python", "Python"],
              ["java", "Java"],
              ["go", "Go"],
              ["ruby", "Ruby"],
            ].map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </fieldset>
      <div className="ad-settings-feedback" aria-live="polite">
        {pending ? "Saving your preference…" : notice}
      </div>
      {error && (
        <p className="ad-resource-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
